import { MAX_WIDTH_CUSTOMIZED_TABLET } from '@components/Hooks/useWidth';
import { CollapsedMode, ContextMode } from '@components/v2/layout/AppTemplate/appTemplate.types';
import { useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApplicationContext } from '../../../../../modules/context/ApplicationContext/ApplicationContext';
import { useSidebarState } from './useSidebarState';
import { useTransientState } from './useTransientState';

interface UseAppTemplateParams {
  collapsedMode: CollapsedMode;
  contextMode: ContextMode;
  onBack?: () => void;
}

export const useAppTemplate = ({ collapsedMode, contextMode, onBack }: UseAppTemplateParams) => {
  const isNested = contextMode === 'nested';
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(`(max-width:${MAX_WIDTH_CUSTOMIZED_TABLET}px)`);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { isActive: isBlurring, trigger: triggerBlur } = useTransientState(80);
  const previousNavigation = useRef({ pathname, isNested });
  const previousCollapsedMode = useRef(collapsedMode);

  const { team, brandId } = useApplicationContext();
  const contextKey = `${team?.id ?? ''}:${brandId ?? ''}`;

  const { effectiveSidebarState, toggleSidebar } = useSidebarState({
    collapsedMode,
    isNested,
    contextKey,
  });

  useEffect(() => {
    if (!isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const previous = previousNavigation.current;
    previousNavigation.current = { pathname, isNested };

    if (previous.isNested !== isNested) {
      triggerBlur();
      return;
    }

    if (previous.pathname !== pathname) {
      setIsMobileOpen(false);
    }
  }, [pathname, isNested, triggerBlur]);

  useEffect(() => {
    if (previousCollapsedMode.current === collapsedMode) {
      return;
    }

    previousCollapsedMode.current = collapsedMode;
    setIsMobileOpen(false);
  }, [collapsedMode]);

  const handleMenuToggle = () => {
    if (isMobile) {
      setIsMobileOpen((prev) => !prev);
      return;
    }

    if (isNested) {
      onBack?.();
      return;
    }

    toggleSidebar();
  };

  const closeMobileSidebar = () => {
    setIsMobileOpen(false);
  };

  return {
    effectiveSidebarState,
    isBlurring,
    handleMenuToggle,
    closeMobileSidebar,
    isMobile,
    isMobileOpen,
  };
};
